import { ProfileRoles } from '@bp/pim-auth-constants';
import { Grid, GridColumn, GridRow, LazyLoader, ModalBottomButtons } from '@bp/ui-components';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { graphql } from 'client/gql';
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { backendApi } from '../../../utils/backendApi';
import { findMatchingProfiles } from '../../../utils/findMatchingProfiles';
import { showUserDbErrorToast } from '../../../utils/toasts';
import { PersonAssignments } from '../modals/ImportTutorsModal';
import { AssignExistingProfile } from './AssignExistingProfile';

const ImportTutorsFormQueryDocument = graphql(`
  query ImportTutorsFormData($where: ProfileWhere!) {
    profiles(where: $where) {
      uuid
      __typename
      firstName
      lastName
      email
      selectName
      foreignRefId
    }
  }
`);

type ImportPersonsFormProps = {
  classIds: number[];
  onImport: (assignments: PersonAssignments | null) => Promise<void> | void;
  onClose: () => void;
};

type MatchTutorsApiResult = Array<{
  personId: number;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
}>;

export const ImportTutorsForm = ({ classIds, onImport, onClose }: ImportPersonsFormProps) => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationConfigContext);

  const { data, isFetching } = useReactQuery<MatchTutorsApiResult>({
    queryKey: ['teachers', classIds],
    queryFn: async () => {
      const response = await backendApi.post(`/procurat/persons/unknown-tutors/`, JSON.stringify(classIds));
      if (!response.ok) {
        throw new Error('Failed to fetch group members');
      }
      if (!response.ok) {
        showUserDbErrorToast();
        onClose();
      } else {
        return await response.json();
      }
    },
  });

  const [{ data: profilesData }] = useQuery({
    query: ImportTutorsFormQueryDocument,
    variables: {
      where: {
        organization: { uuid: organization?.uuid },
        organizationRoles_INCLUDES: ProfileRoles.Teacher,
      },
    },
    pause: !organization,
  });

  // combine incoming data with matching profiles
  const combinedData =
    data?.map((person) => {
      return {
        ...person,
        matching: findMatchingProfiles(profilesData?.profiles, person) ?? [],
      };
    }) ?? [];

  const dataWithMatching = combinedData.filter((item) => item.matching.length > 0).sort();

  if (isFetching)
    return (
      <div>
        {t('procuratImport.fetchingMatchingProfile')}
        <LazyLoader embedded transparent />
      </div>
    );

  //  no matching Profiles found, just import Classes
  if (dataWithMatching.length === 0) {
    onClose();
    onImport(null);
  }

  // we found matching Profiles, let the user assign the Procurat! Teachers to existing BpProfiles before import
  return (
    <Formik<PersonAssignments>
      initialValues={{}}
      onSubmit={() => {
        // no-op
      }}
    >
      {({ setFieldValue, values, isSubmitting, errors, resetForm }) => (
        <Form>
          <div className={'mb-6'}>{t('procuratImport.classImportProfileAssign')}</div>
          <Grid>
            {dataWithMatching.map((person) => (
              <GridRow spacingTop='none' key={person.personId}>
                <GridColumn width={3} align={'start'} justify={'center'}>
                  {person.firstName} {person.lastName}
                </GridColumn>

                <GridColumn width={3} align={'start'} justify={'center'}>
                  <AssignExistingProfile
                    matching={person.matching}
                    handleSelect={(uuid) => setFieldValue(person.personId.toString(), uuid)}
                  />
                </GridColumn>
              </GridRow>
            ))}
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: async () => {
                onClose();
                resetForm();
              },
            }}
            submitButton={{
              text: t('procuratImport.importClasses'),
              callback: async (_e) => {
                onClose();
                await onImport(values);
                resetForm();
              },
            }}
            isLoading={isSubmitting}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
};
